import React from "react";
import PropTypes from "prop-types";
import { DatePicker } from "antd";
import { formatLeadStatus } from "../leadDetailUtils";

export const LeadListToolbar = ({
  heading,
  countLabel,
  selectedLeadCountLabel,
  loadingExcel,
  onExportCsv,
  onExportExcel,
  onBulkAssign,
  hasLeads,
  selectedLeadCount,
  searchQuery,
  setSearchQuery,
  dateRange,
  setDateRange,
  status,
  setStatus,
  spaceType,
  setSpaceType,
  statusOptions,
  spaceTypeOptions,
  onClearFilters,
}) => (
  <>
    <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-lg font-semibold text-gray-900">{heading}</div>
        <div className="text-xs text-gray-500">{countLabel}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-600">{selectedLeadCountLabel}</span>
        <button
          type="button"
          onClick={onExportCsv}
          disabled={!hasLeads}
          className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
        >
          Export CSV
        </button>
        <button
          type="button"
          onClick={onExportExcel}
          disabled={loadingExcel}
          className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
        >
          {loadingExcel ? "Exporting..." : "Export Excel"}
        </button>
        <button
          type="button"
          onClick={onBulkAssign}
          disabled={selectedLeadCount === 0}
          className="rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        >
          Assign Selected
        </button>
      </div>
    </div>

    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-5">
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="h-9 w-full bg-white border border-gray-300 text-gray-900 text-sm rounded-md px-3 focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          placeholder="Search lead or partner..."
        />

        <DatePicker.RangePicker
          onChange={(dates) => {
            setDateRange(dates || []);
          }}
          format="YYYY-MM-DD"
          className="w-full h-9"
          value={dateRange}
        />

        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="h-9 w-full text-sm bg-white border border-gray-300 rounded-md px-2 focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
        >
          {statusOptions.map((option) => (
            <option key={option.value || "all-status"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          value={spaceType}
          onChange={(event) => setSpaceType(event.target.value)}
          className="h-9 w-full text-sm bg-white border border-gray-300 rounded-md px-2 focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
        >
          {spaceTypeOptions.map((option) => (
            <option key={option.value || "all-space"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={onClearFilters}
            className="text-xs px-3 h-8 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  </>
);

LeadListToolbar.propTypes = {
  heading: PropTypes.string.isRequired,
  countLabel: PropTypes.string.isRequired,
  selectedLeadCountLabel: PropTypes.string.isRequired,
  loadingExcel: PropTypes.bool.isRequired,
  onExportCsv: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func.isRequired,
  onBulkAssign: PropTypes.func.isRequired,
  hasLeads: PropTypes.bool.isRequired,
  selectedLeadCount: PropTypes.number.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  dateRange: PropTypes.array.isRequired,
  setDateRange: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  setStatus: PropTypes.func.isRequired,
  spaceType: PropTypes.string.isRequired,
  setSpaceType: PropTypes.func.isRequired,
  statusOptions: PropTypes.array.isRequired,
  spaceTypeOptions: PropTypes.array.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

export const LeadListTable = ({
  leads,
  currentPage,
  recordsPerPage,
  selectedLeadIds,
  allCurrentPageSelected,
  onToggleSelectAll,
  onToggleLeadSelection,
  statusBadges,
  onViewLead,
  onAssignLead,
  getLeadAssignee,
  getLeadAssigneeId,
  formatBudget,
  formatDateTime,
}) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm text-left text-gray-700">
      <thead className="text-[11px] text-gray-500 uppercase bg-gray-50 tracking-wide">
        <tr className="border-b border-gray-200">
          <th className="py-2 px-3 whitespace-nowrap">
            <input
              type="checkbox"
              checked={allCurrentPageSelected}
              onChange={onToggleSelectAll}
              aria-label="Select all leads on this page"
            />
          </th>
          <th className="py-2 px-3 whitespace-nowrap">Sr No</th>
          <th className="py-2 px-3 whitespace-nowrap">Customer</th>
          <th className="py-2 px-3 whitespace-nowrap">Mobile</th>
          <th className="py-2 px-3 whitespace-nowrap">Address</th>
          <th className="py-2 px-3 whitespace-nowrap">Requirement</th>
          <th className="py-2 px-3 whitespace-nowrap">Space Type</th>
          <th className="py-2 px-3 whitespace-nowrap text-right">Budget</th>
          <th className="py-2 px-3 whitespace-nowrap">Status</th>
          <th className="py-2 px-3 whitespace-nowrap">Channel Partner</th>
          <th className="py-2 px-3 whitespace-nowrap">Team Assignment</th>
          <th className="py-2 px-3 whitespace-nowrap">Action</th>
          <th className="py-2 px-3 whitespace-nowrap">Created At</th>
          <th className="py-2 px-3 whitespace-nowrap">Notes</th>
        </tr>
      </thead>
      <tbody>
        {leads.length > 0 ? (
          leads.map((lead, index) => {
            const notes = String(lead.notes || "").trim();
            const shortNotes = notes.length > 60 ? `${notes.slice(0, 60).trimEnd()}...` : notes;

            return (
              <tr key={lead.id || `${lead.channel_partner_id}-${index}`} className="bg-white border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedLeadIds.includes(String(lead.id))}
                    onChange={() => onToggleLeadSelection(lead.id)}
                    aria-label={`Select lead ${lead.customer_name || lead.id}`}
                  />
                </td>
                <td className="py-2 px-3 whitespace-nowrap">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                <td className="py-2 px-3 whitespace-nowrap font-medium text-gray-900">{lead.customer_name || "-"}</td>
                <td className="py-2 px-3 whitespace-nowrap">{lead.mobile || "-"}</td>
                <td className="py-2 px-3 min-w-[220px]">{lead.address || "-"}</td>
                <td className="py-2 px-3 min-w-[220px]">{lead.requirement_title || "-"}</td>
                <td className="py-2 px-3 whitespace-nowrap uppercase">{lead.space_type || "-"}</td>
                <td className="py-2 px-3 whitespace-nowrap text-right">{formatBudget(lead.budget)}</td>
                <td className="py-2 px-3 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadges[lead.status] || "bg-gray-100 text-gray-700"}`}>
                    {formatLeadStatus(lead.status)}
                  </span>
                </td>
                <td className="py-2 px-3 min-w-[220px]">
                  <div className="font-medium text-gray-900">{lead.channel_partner_name || "-"}</div>
                  <div className="text-xs text-gray-500">{lead.channel_partner_company_name || "-"}</div>
                  <div className="text-xs text-gray-500">{lead.channel_partner_mobile || "-"}</div>
                </td>
                <td className="py-2 px-3 min-w-[220px]">
                  <div className="font-medium text-gray-900">{getLeadAssignee(lead) || "Unassigned"}</div>
                  <div className="text-xs text-gray-500">
                    {getLeadAssigneeId(lead)
                      ? `${lead.assigned_admin_mobile || "Assigned admin"}`
                      : "Waiting for admin assignment"}
                  </div>
                  {lead.assigned_by_admin_name && (
                    <div className="text-xs text-gray-500">{`By: ${lead.assigned_by_admin_name}`}</div>
                  )}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onViewLead(lead)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => onAssignLead(lead)}
                      className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                    >
                      {getLeadAssigneeId(lead) ? "Reassign" : "Assign"}
                    </button>
                  </div>
                </td>
                <td className="py-2 px-3 whitespace-nowrap">{formatDateTime(lead.created_at)}</td>
                <td className="py-2 px-3 min-w-[240px]">
                  {shortNotes || "-"}
                  {notes.length > 60 && (
                    <button className="ml-2 text-blue-600 hover:underline" onClick={() => onViewLead(lead)}>
                      View
                    </button>
                  )}
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
          <td colSpan={14} className="py-6 text-center text-gray-500">
              No channel partner leads found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

LeadListTable.propTypes = {
  leads: PropTypes.array.isRequired,
  currentPage: PropTypes.number.isRequired,
  recordsPerPage: PropTypes.number.isRequired,
  selectedLeadIds: PropTypes.array.isRequired,
  allCurrentPageSelected: PropTypes.bool.isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
  onToggleLeadSelection: PropTypes.func.isRequired,
  statusBadges: PropTypes.object.isRequired,
  onViewLead: PropTypes.func.isRequired,
  onAssignLead: PropTypes.func.isRequired,
  getLeadAssignee: PropTypes.func.isRequired,
  getLeadAssigneeId: PropTypes.func.isRequired,
  formatBudget: PropTypes.func.isRequired,
  formatDateTime: PropTypes.func.isRequired,
};

const isActiveAdmin = (admin) => Number(admin?.status) === 1 || String(admin?.status).toLowerCase() === "active";

export const LeadAssignOptions = ({
  admins,
  adminLoading,
  adminLoadError,
  selectedAdminId,
  onChangeAdmin,
}) => {
  const activeAdmins = admins.filter(isActiveAdmin);

  return (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">Admin</label>
    <select
      value={selectedAdminId}
      onChange={(event) => onChangeAdmin(event.target.value)}
      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
      disabled={adminLoading}
    >
      <option value="">{adminLoading ? "Loading admins..." : "Select admin"}</option>
      {activeAdmins.map((admin) => (
        <option key={admin.id} value={String(admin.id)}>
          {[admin.name, admin.role_title, admin.mobile_no || admin.mobile].filter(Boolean).join(" - ")}
        </option>
      ))}
    </select>
    {adminLoadError && <div className="mt-2 text-xs text-red-600">{adminLoadError}</div>}
    {!adminLoading && !adminLoadError && activeAdmins.length === 0 && (
      <div className="mt-2 text-xs text-gray-500">No active admins were found.</div>
    )}
  </div>
  );
};

LeadAssignOptions.propTypes = {
  admins: PropTypes.array.isRequired,
  adminLoading: PropTypes.bool.isRequired,
  adminLoadError: PropTypes.string.isRequired,
  selectedAdminId: PropTypes.string.isRequired,
  onChangeAdmin: PropTypes.func.isRequired,
};
